import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
// api + models
import eventAPI from '@/api/event';
// styles + types + utils + configs
import styles from './styles.module.scss';
import { ImageType } from './types';
import FieldLabel from '../FieldLabel';
import FormValidationErrorTooltip from '../../FormValidationErrorTooltip';
import classnames from 'classnames';
import { IMAGE_URL } from '@/config';
import TooltipDiv from '@/components/ui/icons/TooltipDiv';

const ImageUploadField = (props) => {
  let { value, imageType = ImageType.IMAGE, labelStyles, previewImage = false, isProfilePic = false } = props;
  const { error, label, onChange, submitFailed, readOnly, isMandatory, tooltip, isLastElement } = props;
  let { fileType = "jpg,jpeg,png,gif", maxFileSize = 4, minWidth, maxWidth, minHeight, maxHeight } = props;
  const [uploadedImageFileName, setUploadedImageFileName] = useState(value || '');
  const [imageUploading, setImageUpLoading] = useState(false);
  const [imageReadyToDisplay, setImageReadyToDisplay] = useState(false);
  const dispatch = useDispatch();

  const validationError = submitFailed && error;

  if (value === null) {
    value = '';
  } else if (value) {
    const lastDotIndex = value.lastIndexOf('.');
    const fileExtension = value.substring(lastDotIndex + 1);
    if (
      !['jpg', 'jpeg', 'png', 'gif'].includes(fileExtension.toLocaleLowerCase().trim())
    ) {
      value = '';
    }
  }

  useEffect(() => {
    setUploadedImageFileName(value);
  }, [value]);

  const getImageURL = fieldValue => {
    if (!fieldValue) return fieldValue;

    if (!fieldValue.includes(IMAGE_URL)) {
      return `${IMAGE_URL}${fieldValue}`;
    }

    return fieldValue;
  };

  const handleImageLoad = () => {
    setImageReadyToDisplay(true);
    setImageUpLoading(false);
  };

  const resetState = () => {
    setUploadedImageFileName('');
    setImageReadyToDisplay(false);
    setImageUpLoading(false);
    setUploadedImageFileName('');
    onChange('');
  };

  const getBackendImageUploadPermission = async (fileName: string) => {
    const { data } = await eventAPI.getImageUploadUrl(fileName);
    return data;
  };

  const uploadFileToS3 = async (s3Url: string, file: any) => {
    const s3UploadResponse = await eventAPI.uploadS3Image(s3Url, file);
    return s3UploadResponse;
  };

  const handleFileChoice = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setImageUpLoading(true);
    e.preventDefault();

    var fileUpload = e.target;
    if (typeof (fileUpload.files) === "undefined") {
      dispatch({ type: 'global/addDangerToast', payload: { description: 'This browser does not support HTML5.' } });
      resetState();
      return;
    }

    let fileTypes = fileType.split(',');
    const chosenFile = fileUpload.files[0];
    if(!chosenFile){
      dispatch({ type: 'global/addDangerToast', payload: { description: 'Please select a valid Image file.' } });
      setImageUpLoading(false);
      return;
    }
    const fileUUID = uuidv4();
    const { name: chosenFileName } = chosenFile;
    const lastDotIndex = chosenFileName.lastIndexOf('.');
    const fileExtension = chosenFileName.substring(lastDotIndex + 1);
    const fileName = `${fileUUID}.${fileExtension}`;
    const blob = chosenFile.slice(0, chosenFile.size, chosenFile.type);
    const renamedFile = new File([blob], fileName, { type: chosenFile.type });

    if (!fileTypes.includes(fileExtension.toLocaleLowerCase().trim())) {
      dispatch({ type: 'global/addDangerToast', payload: { description: 'Please select a valid Image file.' } });
      setImageUpLoading(false);
      return;
    }

    if ((chosenFile.size / Math.pow(1024, 2)) > maxFileSize) {
      dispatch({ type: 'global/addDangerToast', payload: { description: `Please select image file below ${maxFileSize} MB.` } });
      setImageUpLoading(false);
      return
    }

    var reader = new FileReader();
    reader.readAsDataURL(chosenFile);
    reader.onload = async function (e) {
      var image = new Image();
      image.src = e.target.result;
      image.onload = async function () {
        var height = this.height;
        var width = this.width;

        if (minWidth && width < minWidth) {
          dispatch({ type: 'global/addDangerToast', payload: { description: `File minimum width should be ${minWidth} pixels` } });
          setImageUpLoading(false);
          return;
        }
        if (minHeight && height < minHeight) {
          dispatch({ type: 'global/addDangerToast', payload: { description: `File minimum height should be ${minHeight} pixels` } });
          setImageUpLoading(false);
          return;
        }
        if (maxWidth && width > maxWidth) {
          dispatch({ type: 'global/addDangerToast', payload: { description: `File maximum width should be ${maxWidth} pixels` } });
          setImageUpLoading(false);
          return;
        }
        if (maxHeight && height > maxHeight) {
          dispatch({ type: 'global/addDangerToast', payload: { description: `File maximum height should be ${maxHeight} pixels` } });
          setImageUpLoading(false);
          return;
        }

        // get the pre-signed request from the backend
        const { presignedPutUrl: s3UploadUrl } = await getBackendImageUploadPermission(fileName);
        // upload the file to s3
        const uploadedS3File = await uploadFileToS3(s3UploadUrl, renamedFile);
        if (uploadedS3File) {
          setUploadedImageFileName(fileName);
          onChange(fileName);
        } else {
          console.log("Oops there's an error when uploading file to s3. :(")
          setImageUpLoading(false);
        }
      };
    }
  };

  const containerWithValidationMessage = classnames(styles.container, {
    [styles.addMarginRight]: !isLastElement,
  });

  const labelText = imageType === ImageType.LOGO ? 'Logo' : 'Image';
  const showPreview = !!previewImage && !!value;

  return (
    <div className={containerWithValidationMessage}>
      <div className={styles.titleLabelContainer}>
        {
          label && label.length ? (
            <FieldLabel value={label} className={labelStyles} />
          ) : null
        }
        {
          isMandatory &&
          <FieldLabel value={`*`} className={labelStyles} />
        }
        {
          label && label.length && tooltip ? (
            <TooltipDiv dataFor={`tooltip_image_upload_${label}`} tooltipText={tooltip} />
          ) : null
        }
      </div>
      {showPreview && (
        <div className={isProfilePic? styles.profilePicView : styles.imagePreview}>
          <img className={styles.containedImage} src={getImageURL(value)} />
          <input
            className={`${styles.fileChoiceInputField}`}
            onChange={e => handleFileChoice(e)}
            type="file"
            accept="image/x-png,image/jpeg,image/gif"
            disabled={readOnly}
            title={' '}
          />
        </div>
      )}
      {!showPreview && (
        <div className={styles.imageUploadField}>
          {imageType === ImageType.LOGO ? <PNGImageIcon /> : <AddImageIcon />}
          <div className={styles.imageUploadText}>
            {value ? `Replace ${labelText}` : `Upload ${labelText}`}
          </div>
          <input
            className={`${styles.fileChoiceInputField}`}
            onChange={e => handleFileChoice(e)}
            type="file"
            accept="image/x-png,image/jpeg,image/gif"
            disabled={readOnly}
            title={' '}
          />
        </div>
      )}
      {validationError && <FormValidationErrorTooltip value={error} />}
    </div>
  );
};

const PNGImageIcon = () => {
  return (
    <svg
      width="20"
      height="14"
      viewBox="0 0 20 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect x="1" y="1" width="18" height="12" rx="3" stroke="black" />
      <path
        d="M4.3744 9V4.69231H6.07392C6.40064 4.69231 6.67899 4.75471 6.90895 4.87951C7.13892 5.0029 7.3142 5.17468 7.4348 5.39483C7.55679 5.61358 7.61779 5.86599 7.61779 6.15204C7.61779 6.4381 7.55609 6.6905 7.43269 6.90925C7.3093 7.128 7.13051 7.29838 6.89633 7.42037C6.66356 7.54237 6.38171 7.60337 6.05078 7.60337H4.96755V6.8735H5.90355C6.07883 6.8735 6.22326 6.84335 6.33684 6.78305C6.45182 6.72135 6.53736 6.63652 6.59345 6.52855C6.65094 6.41917 6.67969 6.29367 6.67969 6.15204C6.67969 6.00901 6.65094 5.88421 6.59345 5.77764C6.53736 5.66967 6.45182 5.58624 6.33684 5.52734C6.22186 5.46705 6.07602 5.4369 5.89934 5.4369H5.28516V9H4.3744ZM11.8124 4.69231V9H11.0258L9.15167 6.28876H9.12012V9H8.20936V4.69231H9.00864L10.868 7.40144H10.9059V4.69231H11.8124ZM15.4213 6.08474C15.3918 5.98237 15.3505 5.89193 15.2972 5.8134C15.2439 5.73347 15.1787 5.66617 15.1016 5.61148C15.0258 5.55539 14.9389 5.51262 14.8407 5.48317C14.744 5.45373 14.6367 5.439 14.5189 5.439C14.2988 5.439 14.1053 5.49369 13.9384 5.60306C13.7729 5.71244 13.6439 5.87159 13.5514 6.08053C13.4588 6.28806 13.4126 6.54187 13.4126 6.84195C13.4126 7.14203 13.4581 7.39724 13.5493 7.60757C13.6404 7.81791 13.7694 7.97847 13.9363 8.08924C14.1032 8.19862 14.3002 8.25331 14.5273 8.25331C14.7335 8.25331 14.9095 8.21685 15.0553 8.14393C15.2025 8.06961 15.3147 7.96514 15.3918 7.83053C15.4704 7.69591 15.5096 7.53676 15.5096 7.35306L15.6947 7.38041H14.5841V6.69471H16.3867V7.23738C16.3867 7.61599 16.3068 7.94131 16.1469 8.21334C15.9871 8.48397 15.7669 8.69291 15.4865 8.84014C15.206 8.98598 14.8849 9.05889 14.5231 9.05889C14.1193 9.05889 13.7645 8.96985 13.4588 8.79177C13.1531 8.61228 12.9148 8.35777 12.7437 8.02825C12.574 7.69732 12.4892 7.30469 12.4892 6.85036C12.4892 6.5012 12.5397 6.1899 12.6406 5.91647C12.743 5.64163 12.886 5.40885 13.0697 5.21815C13.2534 5.02744 13.4672 4.88231 13.7112 4.78275C13.9552 4.68319 14.2196 4.63341 14.5042 4.63341C14.7482 4.63341 14.9754 4.66917 15.1857 4.74068C15.396 4.8108 15.5825 4.91036 15.7452 5.03936C15.9093 5.16837 16.0432 5.32191 16.1469 5.5C16.2507 5.67668 16.3173 5.87159 16.3468 6.08474H15.4213Z"
        fill="black"
      />
    </svg>
  );
};

const AddImageIcon = () => {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M15.75 11.25V13.5H18V15H15.75V17.25H14.25V15H12V13.5H14.25V11.25H15.75ZM15.756 2.25C16.167 2.25 16.5 2.58375 16.5 2.99475V9.75H15V3.75H3V14.2493L10.5 6.75L12.75 9V11.1217L10.5 8.87175L5.12025 14.25H10.5V15.75H2.244C2.04661 15.7498 1.85737 15.6712 1.71787 15.5316C1.57836 15.392 1.5 15.2026 1.5 15.0052V2.99475C1.50137 2.79778 1.58018 2.60926 1.71938 2.46991C1.85859 2.33056 2.04704 2.25157 2.244 2.25H15.756ZM6 5.25C6.39782 5.25 6.77936 5.40804 7.06066 5.68934C7.34196 5.97064 7.5 6.35218 7.5 6.75C7.5 7.14782 7.34196 7.52936 7.06066 7.81066C6.77936 8.09196 6.39782 8.25 6 8.25C5.60218 8.25 5.22064 8.09196 4.93934 7.81066C4.65804 7.52936 4.5 7.14782 4.5 6.75C4.5 6.35218 4.65804 5.97064 4.93934 5.68934C5.22064 5.40804 5.60218 5.25 6 5.25Z"
        fill="#222222"
      />
    </svg>
  );
};

export default ImageUploadField;
export const ImageTypes = ImageType;
